/**
 * piece-access — what to do when a piece will not load.
 *
 * BUG-071. `GET /pieces/:id` 404s whenever the caller's active workspace is
 * not the piece's, because RLS on `pieces` matches `app.agencyclient_id`
 * exactly. The Piece screen used to answer that by redirecting to the board
 * with `replace`, which discarded the URL and explained nothing — so a piece
 * link shared with a teammate simply dumped them somewhere else.
 *
 * The recovery is: ask which of *your* workspaces holds the piece, switch to
 * it, and load. This module holds the decision so it can be tested, because
 * the part that matters is knowing when to stop: a recovery that retries on
 * its own result is an infinite redirect, and the failure it would replace is
 * merely annoying.
 */

export type PieceAccessAction =
  /** Nothing to do — the piece loaded, or is still loading. */
  | { kind: "none" }
  /** Switch to this workspace and refetch. Attempt this once per piece. */
  | { kind: "switch"; agencyclientId: number }
  /** Out of options: say so, rather than navigating away silently. */
  | { kind: "unavailable"; reason: string };

export interface PieceAccessInput {
  /** True once GET /pieces/:id has failed with 404. */
  notFound: boolean;
  /** The workspace the locate call reported, or null when it found none. */
  locatedIn: number | null | undefined;
  /** The workspace the JWT claim currently names. */
  activeAgencyclientId: number | null | undefined;
  /** Whether recovery has already been attempted for this piece. */
  alreadyAttempted: boolean;
}

export function pieceAccessAction(input: PieceAccessInput): PieceAccessAction {
  if (!input.notFound) return { kind: "none" };

  if (input.alreadyAttempted) {
    // We switched and it still will not load. Stop: switching again would
    // loop, and the honest answer is that we cannot show it.
    return {
      kind: "unavailable",
      reason:
        "This piece could not be opened even after switching to its workspace. It may have been deleted.",
    };
  }

  if (input.locatedIn == null) {
    return {
      kind: "unavailable",
      reason:
        "This piece is in a workspace you don't have access to, or it no longer exists.",
    };
  }

  if (input.locatedIn === input.activeAgencyclientId) {
    // The claim is already right, so the workspace is not the problem —
    // switching would change nothing and hide the real cause.
    return {
      kind: "unavailable",
      reason: "This piece could not be loaded. It may have been deleted.",
    };
  }

  return { kind: "switch", agencyclientId: input.locatedIn };
}
